import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Accommodation,
  Insurance,
  PremiumResidence,
  PrivateLesson,
  ProfessionalImmersionExperience,
  SchoolSupplement,
  Transfer,
  TransferServiceType,
} from '@lx/shared/data-access/products';
import { PremiumActivity } from '@lx/shared/data-access/school';
import { getConfiguration } from '@lx/shared/tokens';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExtrasService, QuotePricesService } from '../../../services';
import { QuoteState, QuoteStore, QuoteToolLiteStore } from '../../../store';
import { SchoolExtras } from '../../../types';
import { PricesDisclaimerComponent } from '../../prices-disclaimer/prices-disclaimer.component';
import { QuoteStepFooterComponent } from '../../quote-step-footer/quote-step-footer.component';
import { SupplementsComponent } from '../../supplements/supplements.component';
import { UrgencyDriverComponent } from '../../urgency-driver/urgency-driver.component';
import { YourCourseComponent } from '../../your-course/your-course.component';
import { QuoteStepAdultsExtrasComponent } from './quote-step-adults-extras/quote-step-adults-extras.component';
import { QuoteStepJuniorsExtrasComponent } from './quote-step-juniors-extras/quote-step-juniors-extras.component';
import { valueIsNullOrUndefined } from '@lx/shared/utils';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  imports: [
    UrgencyDriverComponent,
    CommonModule,
    YourCourseComponent,
    SupplementsComponent,
    PricesDisclaimerComponent,
    ConfirmDialogModule,
    QuoteStepFooterComponent,
    QuoteStepAdultsExtrasComponent,
    QuoteStepJuniorsExtrasComponent,
  ],
  selector: 'lx-quote-step',
  templateUrl: './quote-step.component.html',
  styleUrls: ['./quote-step.component.scss'],
  providers: [ConfirmationService],
})
export class QuoteStepComponent implements OnInit {
  protected mostUrgentDiscountExpiryDate$: Observable<string> | undefined;
  protected urgencyDriverVisible$: Observable<boolean> | undefined;
  protected insurance: Insurance | undefined;
  protected accommodations: Accommodation[];
  protected groupedTransfers: Record<TransferServiceType, Transfer[]> | undefined;
  protected premiumAccommodation: PremiumResidence | undefined;
  protected privateLesson: PrivateLesson | undefined;
  protected activeFlow$: Observable<Flow> | undefined;
  protected unaccompaniedMinorServices: SchoolSupplement[] | undefined;
  protected premiumActivities: PremiumActivity[] | undefined;
  protected accommodationOptional: boolean | undefined;
  protected moreAccommodationsDisclaimerShown: boolean | undefined;
  protected professionalImmersionExperience: ProfessionalImmersionExperience | undefined;
  protected readonly Flows = Flows;

  private readonly quoteStore = inject(QuoteStore);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly extrasService = inject(ExtrasService);
  private readonly quotePricesService = inject(QuotePricesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configuration = getConfiguration();

  ngOnInit() {
    this.mostUrgentDiscountExpiryDate$ = this.quoteStore.mostUrgentDiscountExpiryDate$;

    // urgency driver is controlled primarily by a feature flag, but it also requires the expiry date to be set
    this.urgencyDriverVisible$ = this.mostUrgentDiscountExpiryDate$.pipe(
      map((expiryDate) => {
        return (this.configuration.QUOTE_TOOL_SHOW_PROMOTION_EXPIRY as boolean) && !!expiryDate;
      }),
    );

    this.loadExtras();
    this.activeFlow$ = this.quoteToolLiteStore.activeFlow$;

    this.quotePricesService
      .getQuotePrices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((quoteState: QuoteState) => {
        const { quoteId, displayCurrency } = quoteState;
        this.quoteStore.updateState({ ...quoteState });

        const updatedData: { quoteId: string; selectedCurrency?: string } = { quoteId };
        if (!valueIsNullOrUndefined(displayCurrency)) {
          updatedData.selectedCurrency = displayCurrency;
        }

        this.quoteToolLiteStore.updateState(updatedData);
      });
  }

  private loadExtras(): void {
    this.extrasService
      .findSchoolExtras()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ({
          accommodations,
          insurance,
          professionalImmersionExperience,
          groupedTransfers,
          premiumAccommodation,
          privateLesson,
          unaccompaniedMinorServices,
          premiumActivities,
          accommodationOptional,
          moreAccommodationsDisclaimerShown,
        }: SchoolExtras) => {
          this.accommodations = accommodations;
          this.accommodationOptional = accommodationOptional;
          this.moreAccommodationsDisclaimerShown = moreAccommodationsDisclaimerShown;
          this.insurance = insurance;
          this.professionalImmersionExperience = professionalImmersionExperience;
          this.groupedTransfers = groupedTransfers;
          this.privateLesson = privateLesson;
          this.unaccompaniedMinorServices = unaccompaniedMinorServices;
          this.premiumActivities = premiumActivities;
          this.premiumActivities = premiumActivities;
          this.premiumAccommodation = premiumAccommodation;
        },
      );
  }
}
